/**
 * Settings API Service
 * Handles system settings API calls
 */

import apiClient from './axios';
import { ApiResponse } from './types';

/** Backend routes: GET/PUT /api/v1/settings/... (admin via `can:is-admin`, not under `/admin/*`). */
const ADMIN_SETTINGS = '/settings';

export const settingsApi = {
  /**
   * Get all settings or settings by group (admin)
   */
  getSettings: async (group?: string): Promise<ApiResponse<{ settings: Record<string, any> }>> => {
    const params = group ? { group } : {};
    const response = await apiClient.get<ApiResponse<{ settings: Record<string, any> }>>(ADMIN_SETTINGS, { params });
    return response.data;
  },

  /**
   * Get a specific setting (admin)
   */
  getSetting: async (key: string): Promise<ApiResponse<{ setting: { key: string; value: any; type: string; group: string; description?: string } }>> => {
    const response = await apiClient.get<ApiResponse<{ setting: any }>>(`${ADMIN_SETTINGS}/${key}`);
    return response.data;
  },

  /**
   * Get a public setting (no authentication required)
   */
  getPublicSetting: async (key: string): Promise<ApiResponse<{ setting: { key: string; value: any; type: string; group: string; description?: string } }>> => {
    const response = await apiClient.get<ApiResponse<{ setting: any }>>(`/settings/public/${key}`);
    return response.data;
  },

  /**
   * Update a setting (admin)
   */
  updateSetting: async (key: string, value: any): Promise<ApiResponse<{ setting: { key: string; value: any } }>> => {
    const response = await apiClient.put<ApiResponse<{ setting: any }>>(`${ADMIN_SETTINGS}/${key}`, { value });
    return response.data;
  },

  /**
   * Bulk update multiple settings (admin)
   */
  bulkUpdate: async (settings: Array<{ key: string; value: any }>): Promise<ApiResponse<{ updated: string[]; errors: Array<{ key: string; error: string }> }>> => {
    const response = await apiClient.post<ApiResponse<{ updated: string[]; errors: Array<{ key: string; error: string }> }>>(`${ADMIN_SETTINGS}/bulk-update`, { settings });
    return response.data;
  },

  /**
   * Clear all application cache (admin)
   */
  clearCache: async (): Promise<ApiResponse<{ message: string; cleared: string[] }>> => {
    const response = await apiClient.post<ApiResponse<{ message: string; cleared: string[] }>>(`${ADMIN_SETTINGS}/clear-cache`);
    return response.data;
  },

  /**
   * Optimize database tables (admin)
   */
  optimizeDatabase: async (): Promise<ApiResponse<{ message: string }>> => {
    const response = await apiClient.post<ApiResponse<{ message: string }>>(`${ADMIN_SETTINGS}/optimize-database`);
    return response.data;
  },

  /**
   * Create manual backup (admin)
   */
  createBackup: async (type: 'database' | 'full' = 'database'): Promise<ApiResponse<{ message: string; backup: any }>> => {
    const response = await apiClient.post<ApiResponse<{ message: string; backup: any }>>(`${ADMIN_SETTINGS}/create-backup`, { type });
    return response.data;
  },

  /**
   * List all backups (admin)
   */
  listBackups: async (): Promise<ApiResponse<{ backups: any[]; count: number }>> => {
    const response = await apiClient.get<ApiResponse<{ backups: any[]; count: number }>>(`${ADMIN_SETTINGS}/backups`);
    return response.data;
  },

  /**
   * Test SMS notification (admin; super admin only)
   */
  testSms: async (phone: string, countryCode?: string): Promise<ApiResponse<{
    message: string;
    phone: string;
    note?: string;
  }>> => {
    const response = await apiClient.post<ApiResponse<{
      message: string;
      phone: string;
      note?: string;
    }>>(`${ADMIN_SETTINGS}/test-sms`, {
      phone,
      country_code: countryCode,
    });
    return response.data;
  },

  /**
   * Get access controls configuration (admin)
   */
  getAccessControls: async (): Promise<ApiResponse<{
    access_controls: {
      registration_enabled: boolean;
      email_verification_required: boolean;
      phone_verification_required: boolean;
      two_factor_required: boolean;
      maintenance_mode: boolean;
      api_rate_limit: number;
      max_file_upload_size: number;
      allowed_file_types: string[];
    };
  }>> => {
    const response = await apiClient.get<ApiResponse<{
      access_controls: {
        registration_enabled: boolean;
        email_verification_required: boolean;
        phone_verification_required: boolean;
        two_factor_required: boolean;
        maintenance_mode: boolean;
        api_rate_limit: number;
        max_file_upload_size: number;
        allowed_file_types: string[];
      };
    }>>(`${ADMIN_SETTINGS}/access-controls`);
    return response.data;
  },

  /**
   * Update access controls configuration (admin)
   */
  updateAccessControls: async (controls: {
    registration_enabled?: boolean;
    email_verification_required?: boolean;
    phone_verification_required?: boolean;
    two_factor_required?: boolean;
    maintenance_mode?: boolean;
    api_rate_limit?: number;
    max_file_upload_size?: number;
    allowed_file_types?: string[];
  }): Promise<ApiResponse<{
    message: string;
    updated: string[];
  }>> => {
    const response = await apiClient.put<ApiResponse<{
      message: string;
      updated: string[];
    }>>(`${ADMIN_SETTINGS}/access-controls`, controls);
    return response.data;
  },
};

